import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
    redirectUrl
  )}`;
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(location.state?.error || "");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    const res = await login(email.trim(), password);
    setSubmitting(false);
    if (!res.ok) {
      setErr(res.error);
      toast.error(res.error);
      return;
    }
    toast.success(`Welcome back, ${res.user.name}`);
    navigate("/dashboard", { replace: true });
  };

  return (
    <div data-testid="login-page" className="min-h-screen bg-[#FBF9F6]">
      <Navbar />
      <main className="max-w-md mx-auto px-4 pt-16 pb-24">
        <div className="text-center mb-10 anim-fade-up">
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#2D2926]">
            Welcome back
          </h1>
          <p className="mt-2 text-[#6B655D]">Your Colleague is waiting.</p>
        </div>

        <div className="surface-card p-8 anim-fade-up anim-delay-1">
          <button
            type="button"
            data-testid="google-login-btn"
            onClick={startGoogleLogin}
            className="w-full inline-flex items-center justify-center gap-2 border border-[#E6E2D8] hover:bg-[#F2EFE9] rounded-full px-5 py-3 text-sm font-medium text-[#2D2926] transition-colors"
          >
            <GoogleIcon /> Continue with Google
          </button>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-[#E6E2D8]" />
            <span className="text-xs uppercase tracking-wider text-[#9A938A]">
              or with email
            </span>
            <div className="flex-1 h-px bg-[#E6E2D8]" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#9A938A] mb-2">
                Email
              </label>
              <input
                type="email"
                required
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white border border-[#E6E2D8] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent transition-all placeholder-[#9A938A]"
                placeholder="you@work.com"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#9A938A] mb-2">
                Password
              </label>
              <input
                type="password"
                required
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white border border-[#E6E2D8] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent transition-all placeholder-[#9A938A]"
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>
            {err && (
              <p
                data-testid="login-error"
                className="text-sm text-[#D98074] bg-[#FBEEEA] border border-[#F0CCC4] rounded-xl px-3 py-2"
              >
                {err}
              </p>
            )}
            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-btn"
              className="w-full bg-[#2D2926] text-white hover:bg-[#1A1715] rounded-full px-6 py-3 text-sm font-medium transition-colors disabled:opacity-60"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>
            <div className="text-right">
              <Link
                to="/forgot-password"
                data-testid="login-forgot-link"
                className="text-xs text-[#6B655D] hover:text-[#2D2926] underline underline-offset-4"
              >
                Forgot your password?
              </Link>
            </div>
          </form>
        </div>

        <p className="mt-8 text-center text-sm text-[#6B655D]">
          New here?{" "}
          <Link
            to="/register"
            data-testid="login-go-register"
            className="text-[#2D2926] hover:text-[#C29260] underline underline-offset-4"
          >
            Create an account
          </Link>
        </p>
      </main>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 2.9l5.7-5.7C33.5 6.3 28.9 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.6 16.1 18.9 13 24 13c3 0 5.7 1.1 7.8 2.9l5.7-5.7C33.5 6.3 28.9 4 24 4 16.2 4 9.5 8.5 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c4.8 0 9.3-1.8 12.6-4.8l-5.8-4.9c-2 1.4-4.4 2.2-6.8 2.2-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.4 39.5 16.1 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.7l5.8 4.9C39.9 35.7 44 30.4 44 24c0-1.2-.1-2.4-.4-3.5z"
      />
    </svg>
  );
}
